import { useCallback, useEffect, useMemo, useState } from "react";

export type SubscriptionFeature = "thesiscribe" | "organizer" | "dictation" | "budgeting" | "virtual";

export type SubscriptionAccess = Record<SubscriptionFeature, boolean>;

const DEFAULT_ACCESS: SubscriptionAccess = {
  thesiscribe: true,
  organizer: true,
  dictation: true,
  budgeting: true,
  virtual: true,
};

const features: SubscriptionFeature[] = ["thesiscribe", "organizer", "dictation", "budgeting", "virtual"];

const sanitizeHeaderValue = (value: string) => value.replace(/[^\x00-\xff]/g, "");

const buildFunctionsUrl = () => {
  const override = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
  if (override) return override.replace(/\/$/, "");
  const base = import.meta.env.VITE_SUPABASE_URL;
  if (!base) return null;
  return `${base.replace(/\/$/, "")}/functions/v1`;
};

const normalizeAccess = (payload: unknown): SubscriptionAccess => {
  const parsed = (payload ?? {}) as Record<string, unknown>;
  const resolved: SubscriptionAccess = { ...DEFAULT_ACCESS };
  features.forEach((feature) => {
    const value = parsed?.[feature];
    if (typeof value === "boolean") {
      resolved[feature] = value;
    }
  });
  return resolved;
};

const fetchAccessFromApi = async (): Promise<SubscriptionAccess> => {
  const base = buildFunctionsUrl();
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!base || !anonKey) {
    throw new Error("Missing Supabase configuration");
  }

  // Sanitize to avoid "non ISO-8859-1 code point" errors when Cloudflare injects emojis/non-Latin chars
  const safeAnonKey = sanitizeHeaderValue(String(anonKey));

  const headers = new Headers();
  headers.set("apikey", safeAnonKey);
  headers.set("Authorization", sanitizeHeaderValue(`Bearer ${safeAnonKey}`));

  try {
    const response = await fetch(`${base}/subscriptions-me`, {
      headers,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Unable to load subscription access");
    }
    const json = await response.json();
    if (json?.access) {
      return normalizeAccess(json.access);
    }
    return DEFAULT_ACCESS;
  } catch (err) {
    // Surface a clearer error, but sanitize to avoid leaking keys
    const message = err instanceof Error ? err.message : "Unknown subscription error";
    const hint = message.includes("ISO-8859-1")
      ? "Header had non-Latin characters (check environment secrets for emojis/whitespace)."
      : message;
    throw new Error(hint);
  }
};

export const useSubscriptionAccess = () => {
  const [access, setAccess] = useState<SubscriptionAccess>(DEFAULT_ACCESS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAccess = useCallback(async () => {
    // Check for dev mode bypass FIRST
    const devMode = localStorage.getItem('whipt_dev_mode');
    if (devMode === 'true') {
      console.log('🔓 Dev mode active - bypassing subscription check');
      setAccess({
        thesiscribe: true,
        organizer: true,
        dictation: true,
        budgeting: true,
        virtual: true,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchAccessFromApi();
      setAccess(result);
    } catch (err) {
      console.warn("Falling back to default subscription access", err);
      setAccess(() => {
        const envFallback = (() => {
          const raw = import.meta.env.VITE_DEMO_SUBSCRIPTION_ACCESS;
          if (!raw) return null;
          try {
            return normalizeAccess(JSON.parse(raw));
          } catch (error) {
            console.warn("Unable to parse VITE_DEMO_SUBSCRIPTION_ACCESS", error);
            return null;
          }
        })();
        return envFallback ?? DEFAULT_ACCESS;
      });
      setError(err instanceof Error ? err.message : "Unable to load subscription access");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const accessSummary = useMemo(() => access, [access]);

  return {
    access: accessSummary,
    loading,
    error,
    refetch: loadAccess,
  };
};
